import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';
import { UserContext } from '../context/UserContext';
import { resolveMediaUrl } from '../utils/url';
import { getSocialPlatforms, matchPlatform } from '../utils/socialPlatformUtils';
import './home.css';

function safeExternalUrl(input) {
  const raw = (typeof input === 'string' ? input : String(input ?? '')).trim();
  if (!raw) return null;
  // prevent unsafe/non-navigational schemes
  if (/^(?:javascript|data|vbscript|file|about):/i.test(raw)) return null;

  try {
    const u = new URL(raw);
    if (!/^https?:$/i.test(u.protocol)) return null;
    return u.toString();
  } catch {
    // fall through
  }

  try {
    const u = new URL(`https://${raw.replace(/^\/+/, '')}`);
    if (!/^https?:$/i.test(u.protocol)) return null;
    return u.toString();
  } catch {
    return null;
  }
}

function coerceHandleToUrl(name, rawUrl) {
  const n = String(name ?? '').trim().toLowerCase();
  const raw = (typeof rawUrl === 'string' ? rawUrl : String(rawUrl ?? '')).trim();
  if (!raw) return null;

  const handle = raw.replace(/^@/, '').trim();
  const isPlainHandle = /^[a-z0-9_]{2,30}$/i.test(handle) && !/[\/.:\s]/.test(raw);
  if (!isPlainHandle) return null;

  if (n === 'twitch' || n === 'twitchurl' || n === 'twitchlogin') return `https://www.twitch.tv/${handle}`;
  if (n === 'instagram') return `https://www.instagram.com/${handle}/`;
  if (n === 'tiktok') return `https://www.tiktok.com/@${handle}`;
  if (n === 'youtube') return `https://www.youtube.com/@${handle}`;
  return null;
}

function normalizeSocialLinks(socialLinks) {
  // Some rows may store JSON as string.
  if (typeof socialLinks === 'string' && socialLinks.trim()) {
    const raw = socialLinks.trim();
    if ((raw.startsWith('{') && raw.endsWith('}')) || (raw.startsWith('[') && raw.endsWith(']'))) {
      try {
        return normalizeSocialLinks(JSON.parse(raw));
      } catch {
        // fall through
      }
    }
  }

  if (Array.isArray(socialLinks)) {
    return socialLinks
      .map((entry) => {
        if (!entry) return null;
        if (typeof entry === 'string') return { name: '', url: entry };
        if (typeof entry !== 'object') return null;
        const name = entry.name || entry.platform || entry.type || entry.label || entry.key || '';
        const url = entry.url || entry.link || entry.href || entry.value || entry.handle || '';
        const icon = entry.icon || entry.image || '';
        return { name, url, icon };
      })
      .filter(l => l && String(l.url ?? '').trim());
  }

  if (socialLinks && typeof socialLinks === 'object') {
    return Object.entries(socialLinks)
      .map(([name, value]) => {
        if (value === null || typeof value === 'undefined') return null;
        if (typeof value === 'string') return { name, url: value };
        if (typeof value === 'object') {
          const url = value.url || value.link || value.href || value.value || value.handle || '';
          const icon = value.icon || value.image || '';
          return { name, url, icon };
        }
        return { name, url: String(value) };
      })
      .filter(l => l && String(l.url ?? '').trim());
  }

  return [];
}

function prettifyLinkLabel(name, url) {
  const n = String(name ?? '').trim();
  const lower = n.toLowerCase();
  const map = {
    twitch: 'Twitch',
    twitchurl: 'Twitch',
    twitchlogin: 'Twitch',
    youtube: 'YouTube',
    youtubekanal: 'YouTube',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    discord: 'Discord',
    website: 'Website',
    homepage: 'Website',
    twitter: 'X',
    x: 'X',
  };

  if (lower && map[lower]) return map[lower];
  if (lower && !/^\d+$/.test(lower)) return n;

  try {
    const u = new URL(String(url));
    const host = u.hostname.replace(/^www\./i, '');
    return host || 'Link';
  } catch {
    return 'Link';
  }
}

function getEmbedParents() {
  const host = (typeof window !== 'undefined' ? window.location.hostname : '') || 'localhost';
  const alt = host.startsWith('www.') ? host.slice(4) : `www.${host}`;
  return Array.from(new Set([host, alt].filter(Boolean)));
}

function createSessionId() {
  try {
    const key = 'lurker2_session_id';
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id = (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`).slice(0, 64);
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`.slice(0, 64);
  }
}

export default function LurkerMember() {
  const { username } = useParams();
  const { user } = useContext(UserContext);
  const [data, setData] = useState(null);
  const [profileError, setProfileError] = useState('');

  const [radar, setRadar] = useState(null);
  const [radarError, setRadarError] = useState('');
  const [radarLoading, setRadarLoading] = useState(false);
  const [manualStream, setManualStream] = useState(null);

  const [platforms, setPlatforms] = useState([]);

  const [loadingAssignment, setLoadingAssignment] = useState(true);
  const [assigned, setAssigned] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [assignmentError, setAssignmentError] = useState('');
  const firstAssignmentLoad = useRef(true);
  const expiresMs = useMemo(() => (expiresAt ? new Date(expiresAt).getTime() : 0), [expiresAt]);
  const [nowMs, setNowMs] = useState(Date.now());

  const sessionId = useMemo(() => createSessionId(), []);

  useEffect(() => {
    let active = true;
    getSocialPlatforms().then(list => { if (active) setPlatforms(list || []); }).catch(() => setPlatforms([]));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const onUnload = () => {
      try {
        const base = ((import.meta?.env?.VITE_API_URL || '').trim()) || window.location.origin;
        const urlBase = `${String(base).replace(/\/$/, '')}/lurker2/session/close`;
        try {
          const token = localStorage.getItem('token');
          const qs = new URLSearchParams({ sessionId, page: 'lurker_member' });
          fetch(`${urlBase}?${qs.toString()}`, {
            method: 'GET',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            keepalive: true,
            credentials: 'include',
          }).catch(() => {});
        } catch {}
      } catch {}
    };

    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [sessionId]);

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const loadRadar = async () => {
    try {
      setRadarError('');
      setRadarLoading(true);
      const res = await api.get('/lurker2/radar');
      setRadar(res.data);
    } catch (e) {
      setRadar(null);
      setRadarError(e?.response?.data?.error || e?.message || 'Konnte Live‑Radar nicht laden');
    } finally {
      setRadarLoading(false);
    }
  };

  useEffect(() => {
    loadRadar().catch(() => {});
  }, []);

  const remainingSec = useMemo(() => {
    if (!expiresMs) return null;
    return Math.max(0, Math.floor((expiresMs - nowMs) / 1000));
  }, [expiresMs, nowMs]);

  const profileUserId = manualStream?.userId || assigned?.assignedUserId || null;

  useEffect(() => {
    (async () => {
      setProfileError('');
      if (!profileUserId) {
        setData(null);
        return;
      }
      try {
        const res = await api.get(`/lurker2/member-by-id/${encodeURIComponent(String(profileUserId))}`);
        setData(res.data);
      } catch (e) {
        setData(null);
        setProfileError(e?.response?.data?.error || e?.message || 'Konnte Member nicht laden');
      }
    })();
  }, [profileUserId]);

  async function loadAssignment() {
    const isFirst = firstAssignmentLoad.current;
    if (isFirst) {
      setLoadingAssignment(true);
      setAssignmentError('');
    }
    try {
      const a = await api.get('/lurker2/assignment', { params: { sessionId, page: 'lurker_member' } });
      setAssigned(a.data?.assigned || null);
      setExpiresAt(a.data?.expiresAt || null);
    } catch (e) {
      if (isFirst) setAssignmentError(e?.response?.data?.error || e?.message || 'Konnte Zuteilung nicht laden');
      setAssigned(null);
      setExpiresAt(null);
    } finally {
      if (isFirst) setLoadingAssignment(false);
      firstAssignmentLoad.current = false;
    }
  }

  useEffect(() => {
    loadAssignment().catch(() => {});
    const t = setInterval(() => loadAssignment().catch(() => {}), 25000);
    return () => clearInterval(t);
  }, [sessionId]);

  useEffect(() => {
    if (remainingSec == null) return;
    if (remainingSec > 0) return;
    // Rotation window ended; reloading refreshes assignment & need distribution.
    window.location.reload();
  }, [remainingSec]);

  const activeStream = manualStream || null;
  const twitchLogin = (activeStream?.twitchLogin || assigned?.twitchLogin || '').trim();
  const twitchUrl = twitchLogin ? `https://www.twitch.tv/${twitchLogin}` : '';
  const parents = useMemo(() => getEmbedParents(), []);
  const parent = useMemo(() => encodeURIComponent(parents[0] || 'localhost'), [parents]);
  const embedUrl = twitchLogin ? `https://player.twitch.tv/?channel=${encodeURIComponent(twitchLogin)}&parent=${parent}&autoplay=true&muted=true` : '';
  const chatUrl = twitchLogin ? `https://www.twitch.tv/popout/${encodeURIComponent(twitchLogin)}/chat` : '';
  const activeTitle = (activeStream?.title || assigned?.title || '').trim();
  const activeGameName = (activeStream?.gameName || assigned?.gameName || '').trim();
  const activeViewerCount = (typeof activeStream?.viewerCount !== 'undefined' && activeStream?.viewerCount !== null)
    ? Number(activeStream.viewerCount)
    : (typeof assigned?.viewerCount !== 'undefined' && assigned?.viewerCount !== null ? Number(assigned.viewerCount) : null);
  const chatEmbedUrl = useMemo(() => {
    if (!twitchLogin) return '';
    const ps = (parents && parents.length ? parents : ['localhost']).map(p => String(p || '').trim()).filter(Boolean);
    const parentParams = ps.length ? ps.map(p => `parent=${encodeURIComponent(p)}`).join('&') : 'parent=localhost';
    return `https://www.twitch.tv/embed/${encodeURIComponent(twitchLogin)}/chat?${parentParams}`;
  }, [twitchLogin, parents]);

  const mismatchInfo = useMemo(() => {
    const p = String(username || '').toLowerCase();
    const u = String(user?.username || '').toLowerCase();
    if (!p || !u) return '';
    if (p === u) return '';
    return `Hinweis: Du bist als @${user?.username} eingeloggt. Die Zuteilung richtet sich nach deinem Login (nicht nach der URL @${username}).`;
  }, [user?.username, username]);

  const links = useMemo(() => {
    const raw = normalizeSocialLinks(data?.socialLinks);
    const out = [];
    for (const entry of raw) {
      const safeUrl = safeExternalUrl(entry?.url) || safeExternalUrl(coerceHandleToUrl(entry?.name, entry?.url));
      if (!safeUrl) continue;
      const label = prettifyLinkLabel(entry?.name, safeUrl);

      const plat = matchPlatform(safeUrl, platforms);
      const rawIcon = (entry?.icon && String(entry.icon).trim()) ? String(entry.icon).trim() : (plat?.icon || '');
      const iconSrc = rawIcon ? (rawIcon.startsWith('http') || rawIcon.startsWith('data:') ? rawIcon : resolveMediaUrl(rawIcon)) : '';

      out.push({
        key: `${label}:${safeUrl}`,
        label,
        url: safeUrl,
        iconSrc,
      });
    }
    return out;
  }, [data, platforms]);

  const avatarSrc = useMemo(() => {
    const url = (data?.avatarUrl && String(data.avatarUrl).trim()) ? String(data.avatarUrl).trim() : '/avatars/default-avatar.png';
    return resolveMediaUrl(url);
  }, [data?.avatarUrl]);

  return (
    <div className="home-page">
      <section style={{ width: '100vw', margin: 0, padding: 0, position: 'relative', left: '-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>
          Persönlicher Lurker 2.0
        </h2>
        <p className="hero-desc" style={{ margin: '0 auto 24px', textAlign: 'center', maxWidth: 860 }}>
          Schaue einen zufälligen Stream aus unserer Community. Sei Aktiv im Chat und geb so deinen Support und Liebe für den Streamer-in und das gesamte Netzwerk.
        </p>
      </section>

      <section style={{ width: '100%', maxWidth: 1200, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        {profileError && <div style={{ color: 'tomato', marginBottom: 10 }}>{profileError}</div>}
        {assignmentError && <div style={{ color: 'tomato', marginBottom: 10 }}>{assignmentError}</div>}
        {!!mismatchInfo && <div style={{ color: '#ffd27a', marginBottom: 10, fontSize: 13 }}>{mismatchInfo}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: 16, alignItems: 'start' }}>
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12 }}>
              <h3 style={{ marginTop: 0, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                <span>Aktiver Stream</span>
                <span style={{ fontSize: 12, color: '#bbb' }}>Jetzt Live auf Twitch</span>
              </h3>

              {loadingAssignment && !manualStream && <div>Lade…</div>}
              {!twitchLogin && !loadingAssignment && <div style={{ color: '#bbb' }}>Aktuell kein Stream verfügbar.</div>}

              {twitchLogin && (
                <div style={{ minHeight: 300, position: 'relative', borderRadius: 10, overflow: 'hidden', background: '#0b1220' }}>
                  <div style={{ paddingTop: '56.25%' }} />
                  <iframe
                    title={`twitch-${twitchLogin}`}
                    src={embedUrl}
                    allow="autoplay; fullscreen"
                    frameBorder="0"
                    allowFullScreen
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                  />
                </div>
              )}

              {twitchLogin && (
                <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                  {!!activeTitle && (
                    <div>
                      <div style={{ fontSize: 12, color: '#bbb' }}>Streamtitel</div>
                      <div style={{ fontWeight: 600 }}>{activeTitle}</div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', color: '#bbb', fontSize: 13 }}>
                    {!!activeGameName && <span>Kategorie: <span style={{ color: '#fff' }}>{activeGameName}</span></span>}
                    {activeViewerCount !== null && Number.isFinite(activeViewerCount) && (
                      <span>Zuschauer: <span style={{ color: '#fff' }}>{activeViewerCount.toLocaleString('de-DE')}</span></span>
                    )}
                  </div>
                </div>
              )}

              {twitchUrl && (
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <a className="btn" href={twitchUrl} target="_blank" rel="noreferrer">Auf Twitch öffnen</a>
                  <a className="btn" href={chatUrl} target="_blank" rel="noreferrer">Chat öffnen</a>
                  <button className="btn" onClick={() => window.location.reload()} style={{ padding: '6px 10px' }}>Neu laden</button>
                </div>
              )}

              <div style={{ fontSize: 12, color: '#bbb', marginTop: 8 }}>
                Autoplay startet stumm (Browser‑Regeln). Ton kann im Player aktiviert werden.
              </div>
          </div>

          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12 }}>
            <h3 style={{ marginTop: 0 }}>Chat</h3>
            {loadingAssignment && !manualStream && <div>Lade…</div>}
            {!twitchLogin && !loadingAssignment && <div style={{ color: '#bbb' }}>Aktuell kein Chat verfügbar.</div>}
            {twitchLogin && (
              <div style={{ height: 560, borderRadius: 10, overflow: 'hidden', background: '#0b1220' }}>
                <iframe
                  title={`twitch-chat-${twitchLogin}`}
                  src={chatEmbedUrl}
                  frameBorder="0"
                  scrolling="no"
                  allow="clipboard-write"
                  style={{ width: '100%', height: '100%', border: 0 }}
                />
              </div>
            )}

            {twitchUrl && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <a className="btn" href={chatUrl} target="_blank" rel="noreferrer">Chat in neuem Tab</a>
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: 16, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12 }}>
          <h3 style={{ marginTop: 0 }}>Infos</h3>
          {!data && !profileError && <div style={{ color: '#bbb' }}>Lade Profil…</div>}
          {!data && !!profileError && <div style={{ color: '#bbb' }}>Profil nicht verfügbar.</div>}
          {data && (
            <>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
                <img
                  src={avatarSrc}
                  alt={data.displayName || data.username || 'Avatar'}
                  style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.12)' }}
                />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: '#bbb' }}>Streamer</div>
                  <div style={{ fontWeight: 700, fontSize: 18, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {data.displayName || data.username}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#bbb' }}>AP‑N Profil</div>
              <a className="btn" href={`/u/${data.id}`} style={{ marginTop: 6, display: 'inline-block' }}>Profil öffnen</a>

              {data.bio && (
                <>
                  <div style={{ marginTop: 12, fontSize: 12, color: '#bbb' }}>Beschreibung</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{data.bio}</div>
                </>
              )}

              <div style={{ marginTop: 12, fontSize: 12, color: '#bbb' }}>Social Links</div>
              {links.length === 0 && <div style={{ color: '#bbb' }}>Keine Links.</div>}
              {links.length > 0 && (
                <div style={{ display: 'grid', gap: 6, marginTop: 6, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                  {links.map((l) => (
                    <a key={l.key} className="btn" href={l.url} target="_blank" rel="noreferrer" style={{ justifyContent: 'flex-start', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {l.iconSrc ? (
                        <img src={l.iconSrc} alt={l.label} style={{ width: 22, height: 22 }} />
                      ) : null}
                      <span>{l.label}</span>
                    </a>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ marginTop: 16, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12 }}>
          <h3 style={{ marginTop: 0, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
            <span>Live‑Radar</span>
            <span style={{ fontSize: 12, color: '#bbb' }}>{radar?.now ? `Stand: ${new Date(radar.now).toLocaleString()}` : ''}</span>
          </h3>

          {radarError && <div style={{ color: 'tomato', marginBottom: 10 }}>{radarError}</div>}

          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <button onClick={() => loadRadar().catch(() => {})} disabled={radarLoading}>
              {radarLoading ? 'Aktualisieren…' : 'Aktualisieren'}
            </button>
            {manualStream && (
              <button className="btn" onClick={() => setManualStream(null)} style={{ padding: '6px 10px' }}>
                Manuellen Modus verlassen
              </button>
            )}
            {radar && (
              <div style={{ alignSelf: 'center', fontSize: 12, color: '#bbb' }}>
                Live: {radar.liveCount} · Geplant jetzt: {radar.plannedNowCount}
              </div>
            )}
          </div>

          {!radar && !radarError && <div style={{ color: '#bbb' }}>Lade Live‑Radar…</div>}
          {radar && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                    <th style={{ padding: '8px 6px' }}>Streamer</th>
                    <th style={{ padding: '8px 6px' }}>Kategorie</th>
                    <th style={{ padding: '8px 6px' }}>Streamtitel</th>
                    <th style={{ padding: '8px 6px' }}>Viewer</th>
                    <th style={{ padding: '8px 6px' }}>Geplant</th>
                    <th style={{ padding: '8px 6px' }}>Supportbedarf</th>
                    <th style={{ padding: '8px 6px' }}>Profil</th>
                    <th style={{ padding: '8px 6px' }}>Twitch</th>
                    <th style={{ padding: '8px 6px' }}>Player</th>
                  </tr>
                </thead>
                <tbody>
                  {(radar.live || []).map((x) => {
                    const login = String(x?.twitchLogin || '').trim();
                    const twitchHref = login ? `https://www.twitch.tv/${login}` : '';
                    const internalProfileHref = x?.userId ? `/u/${encodeURIComponent(String(x.userId))}` : '';
                    const isActive = !!login && String(twitchLogin || '').toLowerCase() === login.toLowerCase();
                    const title = String(x?.title || '').trim();
                    const gameName = String(x?.gameName || '').trim();
                    const viewerCount = typeof x?.viewerCount !== 'undefined' && x?.viewerCount !== null ? Number(x.viewerCount) : null;
                    const need = typeof x?.needPercent === 'number' ? x.needPercent : 0;

                    return (
                      <tr key={login || `${x?.userId || ''}:${x?.username || ''}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                        <td style={{ padding: '8px 6px', fontWeight: 600, whiteSpace: 'nowrap' }}>{x.displayName || x.username || login || '—'}</td>
                        <td style={{ padding: '8px 6px', color: '#bbb' }}>{gameName || '—'}</td>
                        <td style={{ padding: '8px 6px', color: '#bbb', minWidth: 220 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 420 }}>{title || '—'}</div>
                        </td>
                        <td style={{ padding: '8px 6px' }}>{viewerCount !== null && Number.isFinite(viewerCount) ? viewerCount.toLocaleString('de-DE') : '—'}</td>
                        <td style={{ padding: '8px 6px' }}>{x.planned ? 'ja' : 'nein'}</td>
                        <td style={{ padding: '8px 6px' }}>{`${need.toFixed(1)}%`}</td>
                        <td style={{ padding: '8px 6px' }}>
                          {internalProfileHref ? (
                            <a className="btn" href={internalProfileHref} style={{ padding: '6px 10px' }}>Intern</a>
                          ) : (
                            <span style={{ color: '#bbb' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '8px 6px' }}>
                          {twitchHref ? (
                            <a className="btn" href={twitchHref} target="_blank" rel="noreferrer" style={{ padding: '6px 10px' }}>Öffnen</a>
                          ) : (
                            <span style={{ color: '#bbb' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '8px 6px' }}>
                          <button
                            className="btn"
                            onClick={() => setManualStream({
                              twitchLogin: login,
                              userId: x?.userId || null,
                              username: x?.username || '',
                              title: x?.title || '',
                              gameName: x?.gameName || '',
                              viewerCount: x?.viewerCount,
                              displayName: x?.displayName || '',
                            })}
                            disabled={!login || isActive}
                            style={{ padding: '6px 10px' }}
                          >
                            {isActive ? 'Aktiv' : 'Laden'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {(radar.live || []).length === 0 && (
                    <tr><td colSpan={9} style={{ padding: 10, color: '#bbb' }}>Keine Live‑Streams gefunden.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
